import { useState } from 'react';
import { api, errorMessage } from '../api/client';
import type { Changes, Organ } from '../api/types';
import { useResource } from '../api/useResource';
import { Field, NumberInput, Panel, Spinner, Status, TextInput, Toggle, summariseChanges } from '../components/ui';

export function OrgansPage() {
  const organs = useResource(() => api<{ organs: Organ[] }>('/v1/admin/organs'));
  const [editing, setEditing] = useState<Organ | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!editing) return;

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const result = await api<{ changes: Changes }>(`/v1/admin/organs/${editing.slug}`, {
        method: 'PATCH',
        body: {
          name: editing.name,
          boardPosition: editing.boardPosition,
          artKey: editing.artKey,
          description: editing.description,
          commonCondition: editing.commonCondition,
          metastasisToSlug: editing.metastasisToSlug,
          active: editing.active,
        },
      });

      setMessage(summariseChanges(result.changes));
      setEditing(null);
      await organs.reload();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  if (organs.loading && !organs.data) return <Spinner />;
  if (organs.error) return <Status error={organs.error} />;

  return (
    <Panel title="Organs" actions={<span className="muted">{organs.data?.organs.length ?? 0} nodes</span>}>
      <p className="muted">
        Board position is the organ's node id in the board graph
        (<code>prisma/seed/data/board_graph.json</code>), not a position on a loop — changing it by
        hand will put the organ on a space the graph does not agree with, so reseed instead.
        “Spreads to” is the rulebook's metastasis table: where cancer at this organ goes if it is
        not cured in time. Deactivating an organ hides it from the app without deleting it, so
        finished matches that reference it still resolve.
      </p>

      <table className="table">
        <thead>
          <tr>
            <th>Slug</th>
            <th>Name</th>
            <th>Board</th>
            <th>Spreads to</th>
            <th>Diseases</th>
            <th>Active</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {organs.data?.organs.map((organ) => (
            <tr key={organ.slug} className={organ.active ? undefined : 'row-inactive'}>
              <td><code>{organ.slug}</code></td>
              <td>{organ.name}</td>
              <td>{organ.boardPosition}</td>
              <td>{organ.metastasisToSlug ?? <span className="muted">never spreads</span>}</td>
              <td>{organ.applicableDiseaseSlugs.length}</td>
              <td>{organ.active ? 'yes' : 'no'}</td>
              <td>
                <button className="button button-ghost" onClick={() => setEditing({ ...organ })}>
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing ? (
        <div className="editor">
          <h3>
            Editing <code>{editing.slug}</code>
          </h3>
          <div className="grid">
            <Field label="Name">
              <TextInput value={editing.name} onChange={(name) => setEditing({ ...editing, name })} />
            </Field>
            <Field label="Board position">
              <NumberInput
                value={editing.boardPosition}
                onChange={(value) => setEditing({ ...editing, boardPosition: value === '' ? 0 : value })}
              />
            </Field>
            <Field label="Art key">
              <TextInput
                value={editing.artKey ?? ''}
                onChange={(artKey) => setEditing({ ...editing, artKey: artKey || null })}
              />
            </Field>
            <Field label="Common condition">
              <TextInput
                value={editing.commonCondition}
                onChange={(commonCondition) => setEditing({ ...editing, commonCondition })}
              />
            </Field>
            <Field label="Cancer spreads to (organ slug, blank = never)">
              <TextInput
                value={editing.metastasisToSlug ?? ''}
                onChange={(slug) =>
                  setEditing({ ...editing, metastasisToSlug: slug.trim() || null })
                }
              />
            </Field>
          </div>

          <Field label="Guide description">
            <textarea
              className="input"
              rows={4}
              value={editing.description}
              onChange={(event) => setEditing({ ...editing, description: event.target.value })}
            />
          </Field>

          <Toggle
            checked={editing.active}
            label="Active (visible to the app)"
            onChange={(active) => setEditing({ ...editing, active })}
          />

          <div className="row">
            <button className="button" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save organ'}
            </button>
            <button className="button button-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <Status error={error} message={message} />
    </Panel>
  );
}
